import { FC } from "react";
import { MdStar, MdStarOutline, MdLocationOn, MdLocalPhone } from "react-icons/md";
import { useTranslation } from "react-i18next";
import {GlobeIcon} from "src/assets/Icons";

export interface HotelIdentityInfoProps {
  hotelName: string;
  hotelRating?: number | null;
  address?: string | null;
  hotelWebsiteUrl?: string | null;
  phoneNumber?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  variant?: "full" | "compact" | "card";
}

const HotelIdentityInfo: FC<HotelIdentityInfoProps> = ({
  hotelName,
  hotelRating,
  address,
  hotelWebsiteUrl,
  phoneNumber,
  latitude,
  longitude,
  variant = "full",
}) => {
  const { t } = useTranslation();
  const mapUrl = latitude != null && longitude != null ? `https://www.google.com/maps?q=${latitude},${longitude}` : null;
  const starCount = hotelRating ? Math.round(hotelRating) : 0;

  if (variant === "card") {
    return (
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="truncate text-lg font-bold leading-6 text-font-default">{hotelName}</h3>
        {address && (
          <div className="text-font-subtle flex min-w-0 items-center gap-1 text-sm">
            <MdLocationOn className="h-3 w-3 shrink-0" />
            <span className="truncate">{address}</span>
          </div>
        )}
        {starCount > 0 && (
          <div className="flex shrink-0 items-center gap-1.5">
            <div className="flex items-center gap-0.5">
              {Array.from({ length: starCount }, (_, i) => (
                <MdStar key={i} className="h-3.5 w-3.5 text-icon-rating-star" />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (variant === "compact") {
    return (
      <>
        <p className="text-base font-medium leading-relaxed text-font-default">{hotelName}</p>
        {address && (
          <div className="flex items-start gap-1">
            <MdLocationOn className="text-font-subtle mt-[3px] h-4 w-4 shrink-0" />
            <p className="font-inter text-font-subtle line-clamp-2 text-sm font-medium">{address}</p>
          </div>
        )}
        {starCount > 0 && (
          <div className="flex items-center gap-0.5 text-icon-rating-star">
            {Array.from({ length: starCount }).map((_, i) => (
              <MdStar key={i} className="h-4 w-4" />
            ))}
          </div>
        )}
      </>
    );
  }

  const starLabel = starCount >= 5 ? `${starCount}-STAR LUXURY` : starCount > 0 ? `${starCount}-STAR` : "";

  return (
    <div>
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <h1 className="text-3xl font-bold leading-tight text-font-default">{hotelName}</h1>
        {starCount > 0 && (
          <div className="flex shrink-0 items-center gap-2">
            <div className="flex items-center text-icon-rating-star">
              {Array.from({ length: 5 }, (_, i) =>
                i < starCount ? (
                  <MdStar key={i} className="h-4 w-4 text-icon-rating-star" />
                ) : (
                  <MdStarOutline key={i} className="h-4 w-4 text-icon-subtle" />
                ),
              )}
            </div>
            <span className="text-font-default text-xs font-bold uppercase tracking-wider">{starLabel}</span>
          </div>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-sm font-medium text-font-subtle">
        {mapUrl ? (
          <a
            className="text-font-active flex items-center gap-1"
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MdLocationOn className="h-4 w-4" />
            {t("Hotels.booking.viewOnMap")}
          </a>
        ) : address ? (
          <span className="flex items-center gap-1">
            <MdLocationOn className="h-4 w-4" />
            {address}
          </span>
        ) : null}
        {hotelWebsiteUrl && (
          <a
            className="text-font-active flex items-center gap-1"
            href={hotelWebsiteUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <GlobeIcon className="h-4 w-4" />
            {t("Hotels.booking.visitWebsite")}
          </a>
        )}
        {phoneNumber && (
          <span className="flex items-center gap-1 text-font-subtle">
            <MdLocalPhone className="h-4 w-4" />
            {phoneNumber}
          </span>
        )}
      </div>
    </div>
  );
};

export default HotelIdentityInfo;